import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as opsApi from "@/features/ops/api";
import type { BadA2A } from "@/features/ops/api";

export default function BadA2APage() {
  const [data, setData] = useState<BadA2A | null>(null);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    try {
      setData(await opsApi.getBadA2A());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load report");
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const clear = async () => {
    setClearing(true);
    try {
      await opsApi.clearA2ACounter();
      toast.success("A2A counter cleared");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not clear counter");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">Clients whose A2A / CAF linkage is broken.</p>
        <Button variant="outline" onClick={clear} disabled={clearing}>
          {clearing ? "Clearing…" : "Clear A2A counter"}
        </Button>
      </div>
      {data?.note && (
        <Alert>
          <AlertTitle>Data source pending</AlertTitle>
          <AlertDescription>{data.note}</AlertDescription>
        </Alert>
      )}
      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Issue</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.rows.length ? (
              data.rows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{String(row.client ?? "—")}</TableCell>
                  <TableCell>{String(row.issue ?? "—")}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="text-muted-foreground py-8 text-center">
                  No broken A2A/CAF linkage found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
